/*
 * OtpInput: six single-digit boxes for TOTP and device codes. Typing moves forward, Backspace moves back,
 * arrows move in reading order, and pasting a whole code fills every box. Always LTR (codes read left to
 * right in both languages). Calls `onComplete` when the last digit lands. `group` draws a separator after
 * every n boxes (visual only; the value never contains it).
 *   <OtpInput value={code} onChange={setCode} onComplete={submit} autoFocus />
 *   <OtpInput length={8} group={4} alphanumeric … />   K7QD – 2MXR
 */
import { Fragment, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useFieldControl } from './Field'

export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  autoFocus,
  disabled,
  invalid,
  className,
  alphanumeric,
  group,
}: {
  value: string
  onChange: (v: string) => void
  onComplete?: (v: string) => void
  length?: number
  autoFocus?: boolean
  disabled?: boolean
  invalid?: boolean
  className?: string
  /** accept letters too (device codes like "K7QD-2MXR" use 8 alphanumerics) */
  alphanumeric?: boolean
  /** draw a separator after every this many boxes (e.g. 4 for "K7QD-2MXR") */
  group?: number
}) {
  const { t } = useTranslation('common')
  const field = useFieldControl()
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const allowed = alphanumeric ? /[A-Za-z0-9]/ : /\d/
  const clean = (s: string) =>
    Array.from(s)
      .filter((c) => allowed.test(c))
      .join('')
      .toUpperCase()
      .slice(0, length)

  const set = (next: string) => {
    onChange(next)
    if (next.length === length) onComplete?.(next)
  }
  const focus = (i: number) => refs.current[Math.max(0, Math.min(length - 1, i))]?.focus()

  const onInput = (i: number, raw: string) => {
    const chars = clean(raw)
    if (!chars) return
    const arr = value.padEnd(length, ' ').split('')
    for (let k = 0; k < chars.length && i + k < length; k++) arr[i + k] = chars[k]!
    const next = arr.join('').replace(/\s+$/, '').replace(/\s/g, '')
    set(clean(next))
    focus(i + chars.length)
  }
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (value[i]) set(value.slice(0, i) + value.slice(i + 1))
      else if (i > 0) {
        set(value.slice(0, i - 1) + value.slice(i))
        focus(i - 1)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focus(i - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focus(i + 1)
    }
  }
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const chars = clean(e.clipboardData.getData('text'))
    if (chars) {
      set(chars)
      focus(chars.length)
    }
  }

  const isInvalid = invalid ?? field?.invalid
  return (
    <div
      dir="ltr"
      role="group"
      aria-label={t('ui.otp.label')}
      aria-describedby={field?.describedBy}
      className={cn('flex items-center justify-center gap-2 sm:gap-2.5', className)}
    >
      {Array.from({ length }, (_, i) => (
        <Fragment key={i}>
          {group && i > 0 && i % group === 0 ? (
            <span
              aria-hidden="true"
              className="h-0.5 w-2.5 shrink-0 rounded-full bg-hairline-strong sm:w-3.5"
            />
          ) : null}
          <input
            ref={(el) => {
              refs.current[i] = el
            }}
            id={i === 0 ? field?.id : undefined}
            inputMode={alphanumeric ? 'text' : 'numeric'}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            autoFocus={autoFocus && i === 0}
            disabled={disabled}
            maxLength={length}
            value={value[i] ?? ''}
            aria-label={t('ui.otp.digit', { n: i + 1, total: length })}
            aria-invalid={isInvalid || undefined}
            onChange={(e) => onInput(i, e.target.value)}
            onKeyDown={(e) => onKey(i, e)}
            onPaste={onPaste}
            onFocus={(e) => e.target.select()}
            className={cn(
              'h-12 w-10 rounded-xl border border-hairline-strong bg-surface text-center font-mono text-20 font-medium text-ink caret-brand-600 shadow-[0_1px_1px_var(--color-control)] transition-[border-color,box-shadow] outline-none sm:h-14 sm:w-12',
              'focus:border-brand-500 focus:ring-4 focus:ring-brand-600/12',
              'disabled:bg-surface-quiet aria-invalid:border-danger-600',
              value[i] && 'border-line bg-surface-soft',
            )}
          />
        </Fragment>
      ))}
    </div>
  )
}
